/**
 * The shared answer to "what system receives goods at this warehouse?".
 *
 * `useWarehouseIntegration` asks it for one warehouse and
 * `useManualWarehouses` asks it for several, so the request, its cache and its
 * 404 handling live here rather than being written twice.
 *
 * `GET /api/warehouse/warehouses/{code}/integration` 404s two different ways —
 * unknown warehouse, and warehouse with no active integration. Neither is an
 * error: both mean nothing external receives there, which is `null`.
 */

/** `warehouse.integration.provider` of a site we operate ourselves. */
export const MANUAL_PROVIDER = 'manual';

export type ApiFetch = (url: string, options?: RequestInit) => Promise<Response>;

const cache = new Map<string, string | null>();
const inFlight = new Map<string, Promise<string | null>>();

/** Whether the provider for `code` is already known without a request. */
export function cachedProvider(code: string): string | null | undefined {
  return cache.get(code);
}

/**
 * The live provider behind `code`, or null when nothing external receives
 * there. Cached for the life of the page — integrations change about as often
 * as warehouses do — and de-duplicated while a request is in flight.
 */
export function fetchWarehouseProvider(
  apiFetch: ApiFetch,
  code: string,
): Promise<string | null> {
  if (cache.has(code)) return Promise.resolve(cache.get(code) ?? null);

  const existing = inFlight.get(code);
  if (existing) return existing;

  const request = (async () => {
    const res = await apiFetch(
      `/api/warehouse/warehouses/${encodeURIComponent(code)}/integration`,
    );
    if (res.status === 404) {
      cache.set(code, null);
      return null;
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.detail || 'Could not read the warehouse integration');
    }
    const body = (await res.json()) as { provider?: string | null };
    const provider = (body?.provider || '').trim().toLowerCase() || null;
    cache.set(code, provider);
    return provider;
  })();

  inFlight.set(code, request);
  return request.finally(() => {
    inFlight.delete(code);
  });
}
